import Papa from 'papaparse';
import cyprus_dsa from '../../assets/data/cyprus_dsa.csv'; // This is likely a URL string
import { BirdData } from '../pages/SpeciesBirdIdentification';

export const getBirdData = async (): Promise<BirdData[]> => {
  try {
    const response = await fetch(cyprus_dsa);
    const csvText = await response.text();

    return new Promise<BirdData[]>((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        delimitersToGuess: [',', '\t', '|', ';'],
        complete: (result: Papa.ParseResult<any>) => {
          try {
            if (result.errors.length > 0) {
              console.warn('CSV parsing warnings:', result.errors);
            }

            const rows = result.data as any[];
            const parsed = rows
              .map((row, index): BirdData | null => {
                const getSafeValue = (key: string): string => {
                  return (row[key] || '').toString().trim();
                };

                const sensorId = getSafeValue('Sencor') || getSafeValue('Sensor') || getSafeValue('sensor_id');
                const month = getSafeValue('Month');
                const year = getSafeValue('Year');
                const commonName = getSafeValue('Common Name') || getSafeValue('common_name');
                const scientificName = getSafeValue('Scientific Name') || getSafeValue('scientific_name');
                const probabilityStr = getSafeValue('Probability');
                const conservationStatus = getSafeValue('Europe Status') || getSafeValue('conservation_status');

                if (!sensorId || !commonName || !scientificName) {
                  return null;
                }

                let probability = (parseFloat(probabilityStr) || 0) * 100;
                probability = Math.min(100, Math.max(0, probability));
                probability = parseFloat(probability.toFixed(2));

                const protections: string[] = [];
                const protectionFields = [
                  { key: 'Directive 2009/147/EC', label: 'Birds Directive' },
                  { key: 'Bern Convention', label: 'Bern Convention' },
                  { key: 'IAS Regulation', label: 'IAS Regulation' },
                  { key: 'Bonn Convention', label: 'Bonn Convention' },
                  { key: 'MoU Raptors', label: 'MoU Raptors' },
                  { key: 'CITES', label: 'CITES' },
                  { key: 'AEWA', label: 'AEWA' },
                  { key: 'EU Trade', label: 'EU Trade' },
                  { key: 'SPA/BD Protocol', label: 'SPA/BD Protocol' },
                  { key: 'Helsinki Convention', label: 'Helsinki Convention' },
                ];

                protectionFields.forEach(({ key, label }) => {
                  const value = getSafeValue(key);
                  if (value && value.toLowerCase() !== 'no' && value !== '0') {
                    protections.push(`${label}: ${value}`);
                  }
                });

                let timestamp: string;
                if (year && month) {
                  const date = new Date(parseInt(year), new Date(`${month} 1, 2000`).getMonth(), 1);
                  timestamp = date.toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  });
                } else {
                  timestamp = new Date().toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  });
                }

                return {
                  key: index,
                  sensorId,
                  timestamp,
                  commonName,
                  scientificName,
                  identificationProbability: probability,
                  conservationStatus: conservationStatus || 'Unknown',
                  protectionStatus: protections,
                };
              })
              .filter((item): item is BirdData => item !== null);
            resolve(parsed);
          } catch (error) {
            console.error('Error processing CSV data:', error);
            reject(
              new Error(`Failed to process bird data: ${error instanceof Error ? error.message : 'Unknown error'}`),
            );
          }
        },
        error: (error: any) => {
          console.error('CSV parsing error:', error);
          reject(new Error(`Failed to parse CSV: ${error.message || 'Unknown parsing error'}`));
        },
      });
    });
  } catch (err) {
    throw new Error('Failed to fetch CSV file');
  }
};
